(() => {
  if (customElements.get('nx-essence-media')) return;
  const safeMediaURL = (value, video = false) => {
    try {
      const url = new URL(value, location.href);
      const extension = video ? /\.mp4$/i : /\.(?:jpe?g|png|webp|avif)$/i;
      const shopifyFile = url.hostname === 'cdn.shopify.com' && /^\/(?:videos\/|s\/files\/)/.test(url.pathname);
      const themeVideo = video && url.origin === location.origin &&
        /^\/cdn\/shop\/t\/\d+\/assets\/nx-(?:(?:anelo|luvani|poka|yuma|oudian|masar|vitias|tierra|amoral|mansa|naki|kama|otimo)-essence-speed-260915|anelo-speed-800-260915|melos-essence-800-260925)\.mp4$/.test(url.pathname);
      return url.protocol === 'https:' && !url.port && !url.username && !url.password &&
        (shopifyFile || themeVideo) && extension.test(url.pathname) ? url.href : '';
    } catch (_) { return ''; }
  };
  class NXEssenceMedia extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.video = this.querySelector('[data-essence-video]');
      this.stage = this.querySelector('[data-essence-stage]');
      this.home = this.querySelector('[data-essence-home]');
      this.dialog = this.querySelector('dialog');
      this.dialogSlot = this.querySelector('[data-essence-slot]');
      this.playButton = this.querySelector('[data-essence-play]');
      this.expandButton = this.querySelector('[data-essence-expand]');
      this.closeButton = this.querySelector('[data-essence-close]');
      this.status = this.querySelector('[data-essence-status]');
      if (!this.video || !this.stage || !this.home || !this.dialog || !this.dialogSlot || !this.playButton || !this.expandButton || !this.closeButton || !this.status) return;
      this.active = true;
      this.events = new AbortController();
      this.cleanups = [];
      this.intent = this.intent || 'auto';
      this.requestId = 0;
      this.playRequested = false;
      this.suspended = false;
      this.blocked = false;
      this.failed = false;
      this.visible = false;
      this.url = safeMediaURL(this.dataset.videoUrl, true);
      this.posterImage = this.querySelector('[data-essence-poster]');
      this.posterSizes = this.posterImage?.getAttribute('sizes');
      this.posterReady = !this.posterImage;
      this.posterFrame = 0;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: this.events.signal });
      const releasePoster = () => {
        if (this.posterReady || this.posterFrame) return;
        this.posterFrame = requestAnimationFrame(() => {
          this.posterFrame = requestAnimationFrame(() => {
            this.posterFrame = 0;
            this.posterReady = true;
            this.sync();
          });
        });
      };
      if (this.posterImage) {
        listen(this.posterImage, 'load', releasePoster);
        listen(this.posterImage, 'error', releasePoster);
        if (this.posterImage.complete) releasePoster();
      }
      listen(this.playButton, 'click', () => {
        if (!this.video.paused || this.playRequested) {
          this.intent = 'pause';
        } else {
          this.intent = 'play';
          this.explicitPlay = true;
          this.blocked = false;
          if (this.failed) {
            this.failed = false;
            this.video.removeAttribute('src');
            this.video.load();
          }
          this.status.hidden = true;
          this.status.textContent = '';
        }
        this.sync();
      });
      listen(this.expandButton, 'click', () => this.openViewer());
      listen(this.closeButton, 'click', () => this.dialog.close());
      listen(this.dialog, 'cancel', event => { event.preventDefault(); this.dialog.close(); });
      listen(this.dialog, 'close', () => this.restoreViewer());
      listen(this.video, 'playing', () => {
        if (!this.wantsPlayback()) { this.sync(); return; }
        this.stage.dataset.ready = '';
        this.updateControls();
      });
      listen(this.video, 'pause', () => this.updateControls());
      listen(this.video, 'error', () => this.fail());
      listen(document, 'visibilitychange', () => this.sync());
      listen(window, 'pagehide', () => { this.suspended = true; this.sync(); });
      listen(window, 'pageshow', () => { this.suspended = false; this.sync(); });
      this.dialogObserver = new MutationObserver(records => {
        const containsDialog = node => node.nodeType === 1 &&
          (node.matches('dialog[open]') || node.querySelector('dialog[open]'));
        if (records.some(record => record.type === 'attributes'
          ? record.target.tagName === 'DIALOG'
          : [...record.addedNodes, ...record.removedNodes].some(containsDialog))) this.sync();
      });
      this.dialogObservationActive = false;
      const preferenceChange = () => { this.explicitPlay = false; this.sync(); };
      if (this.motion.addEventListener) listen(this.motion, 'change', preferenceChange);
      else {
        this.motion.addListener(preferenceChange);
        this.cleanups.push(() => this.motion.removeListener(preferenceChange));
      }
      if (this.connection && this.connection.addEventListener) listen(this.connection, 'change', preferenceChange);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.2;
          this.sync();
        }, { threshold: [0, 0.2] });
        this.observer.observe(this.home);
      }
      this.playButton.hidden = !this.url;
      this.expandButton.hidden = typeof this.dialog.showModal !== 'function';
      if (!this.url) this.fail();
      this.updateControls();
    }
    canAttemptPlayback() {
      const constrained = this.motion.matches || (this.connection &&
        (this.connection.saveData || ['slow-2g', '2g'].includes(this.connection.effectiveType)));
      return this.active && !this.suspended && !document.hidden && !this.failed && !this.blocked &&
        (this.posterReady || this.explicitPlay) &&
        this.intent !== 'pause' && (this.dialog.open || this.visible || this.explicitPlay && !this.observer) &&
        (!constrained || this.explicitPlay);
    }
    wantsPlayback() {
      return this.canAttemptPlayback() &&
        (!NXEssenceMedia.viewer || NXEssenceMedia.viewer === this) &&
        !Array.from(document.querySelectorAll('dialog[open]')).some(dialog => dialog !== this.dialog);
    }
    sync() {
      if (!this.active) return;
      const observeDialogs = !!this.canAttemptPlayback();
      if (observeDialogs !== this.dialogObservationActive) {
        this.dialogObservationActive = observeDialogs;
        if (observeDialogs) this.dialogObserver.observe(document.documentElement, {
          subtree: true, childList: true, attributes: true, attributeFilter: ['open']
        });
        else this.dialogObserver.disconnect();
      }
      if (!this.wantsPlayback()) {
        this.requestId += 1;
        this.playRequested = false;
        this.video.pause();
        this.updateControls();
        return;
      }
      if (!this.video.paused || this.playRequested) return;
      if (!this.video.getAttribute('src')) this.video.src = this.url;
      this.playRequested = true;
      const request = ++this.requestId;
      this.updateControls();
      const result = this.video.play();
      if (!result || !result.then) { this.playRequested = false; return; }
      result.then(() => {
        if (request !== this.requestId) return;
        this.playRequested = false;
        if (!this.wantsPlayback()) this.video.pause();
        this.updateControls();
      }).catch(error => {
        if (request !== this.requestId) return;
        this.playRequested = false;
        if (error.name === 'NotAllowedError') this.blocked = true;
        else if (error.name !== 'AbortError') this.fail();
        this.updateControls();
      });
    }
    updateControls() {
      const playing = !this.video.paused || this.playRequested;
      const label = playing ? this.dataset.labelPause : this.dataset.labelPlay;
      if (this.playButton.hasAttribute('data-playing') !== playing) this.playButton.toggleAttribute('data-playing', playing);
      if (this.playButton.getAttribute('aria-label') !== label) this.playButton.setAttribute('aria-label', label);
    }
    fail() {
      this.failed = true;
      this.requestId += 1;
      this.playRequested = false;
      this.video.pause();
      delete this.stage.dataset.ready;
      this.status.textContent = this.dataset.labelError;
      this.status.hidden = false;
      this.updateControls();
    }
    openViewer() {
      if (this.dialog.open) return;
      const previous = NXEssenceMedia.viewer;
      if (previous && previous !== this) {
        previous.dialog.close();
        previous.restoreViewer(false);
      }
      this.returnFocus = document.activeElement;
      this.scrollPosition = [window.scrollX, window.scrollY];
      try { this.dialog.showModal(); } catch (_) { return; }
      NXEssenceMedia.viewer = this;
      if (previous && previous !== this) previous.sync();
      this.savedStyles = [];
      const set = (element, property, value) => {
        this.savedStyles.push([element, property, element.style.getPropertyValue(property), element.style.getPropertyPriority(property)]);
        element.style.setProperty(property, value);
      };
      const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const padding = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      set(document.documentElement, 'scroll-behavior', 'auto');
      set(document.documentElement, 'overflow', 'hidden');
      set(document.body, 'position', 'fixed');
      set(document.body, 'top', `${-this.scrollPosition[1]}px`);
      set(document.body, 'left', `${-this.scrollPosition[0]}px`);
      set(document.body, 'width', '100%');
      set(document.body, 'box-sizing', 'border-box');
      if (gap) set(document.body, 'padding-right', `${padding + gap}px`);
      this.dialogSlot.appendChild(this.stage);
      if (this.posterImage && this.posterSizes) this.posterImage.sizes = '(min-width: 840px) 800px, 100vw';
      this.closeButton.focus({ preventScroll: true });
      this.sync();
    }
    restoreViewer(restoreFocus = true) {
      if (!this.savedStyles) return;
      this.home.appendChild(this.stage);
      if (this.posterImage && this.posterSizes) this.posterImage.sizes = this.posterSizes;
      const scrollStyle = this.savedStyles.shift();
      this.savedStyles.reverse().forEach(([element, property, value, priority]) => {
        if (value) element.style.setProperty(property, value, priority);
        else element.style.removeProperty(property);
      });
      this.savedStyles = null;
      window.scrollTo({ left: this.scrollPosition[0], top: this.scrollPosition[1], behavior: 'instant' });
      if (restoreFocus && this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus({ preventScroll: true });
      if (scrollStyle[2]) scrollStyle[0].style.setProperty(scrollStyle[1], scrollStyle[2], scrollStyle[3]);
      else scrollStyle[0].style.removeProperty(scrollStyle[1]);
      if (NXEssenceMedia.viewer === this) NXEssenceMedia.viewer = null;
      this.sync();
    }
    disconnectedCallback() {
      if (!this.active) return;
      this.active = false;
      this.requestId += 1;
      this.events.abort();
      cancelAnimationFrame(this.posterFrame);
      this.posterFrame = 0;
      this.observer?.disconnect();
      this.dialogObserver?.disconnect();
      this.dialogObservationActive = false;
      this.cleanups.forEach(cleanup => cleanup());
      if (this.dialog.open) this.dialog.close();
      this.restoreViewer(false);
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      delete this.stage.dataset.ready;
    }
  }
  customElements.define('nx-essence-media', NXEssenceMedia);
})();
